import type { Quaternion, Vector3 } from '@repo/types/maps/map-3d-types';
import * as MapLibre from 'maplibre-gl';
import { Euler, type EulerOrder, MathUtils, Quaternion as ThreeQuaternion } from 'three';

export function metersToLatLng(xMeters: number, yMeters: number, zMeters: number, baseLat: number, baseLng: number) {
    const baseCoord = MapLibre.MercatorCoordinate.fromLngLat([baseLng, baseLat], 0);
    const metersPerUnit = baseCoord.meterInMercatorCoordinateUnits();

    const newX = baseCoord.x + xMeters * metersPerUnit;
    const newY = baseCoord.y - yMeters * metersPerUnit;
    const newZ = baseCoord.z + zMeters * metersPerUnit;

    const newCoord = new MapLibre.MercatorCoordinate(newX, newY, newZ);
    const lngLat = newCoord.toLngLat();
    const elevation = newCoord.toAltitude();

    return {
        lng: lngLat.lng,
        lat: lngLat.lat,
        elevation,
    };
}

export function latLngToMeters(
    baseLat: number,
    baseLng: number,
    targetLat: number,
    targetLng: number,
    targetElevation: number = 0,
) {
    const baseCoord = MapLibre.MercatorCoordinate.fromLngLat([baseLng, baseLat], 0);
    const targetCoord = MapLibre.MercatorCoordinate.fromLngLat([targetLng, targetLat], targetElevation);

    const metersPerUnit = baseCoord.meterInMercatorCoordinateUnits();

    const xMeters = (targetCoord.x - baseCoord.x) / metersPerUnit;
    const yMeters = -(targetCoord.y - baseCoord.y) / metersPerUnit;
    const zMeters = (targetCoord.z - baseCoord.z) / metersPerUnit;

    return { xMeters, yMeters, zMeters };
}

export function quaternionToEuler(q: Quaternion, order: EulerOrder = 'XYZ'): Vector3 {
    const x = Number.isFinite(q.x) ? q.x : 0;
    const y = Number.isFinite(q.y) ? q.y : 0;
    const z = Number.isFinite(q.z) ? q.z : 0;
    const w = Number.isFinite(q.w) ? q.w : 1;

    const quaternion = new ThreeQuaternion(x, y, z, w);
    quaternion.normalize();

    const euler = new Euler().setFromQuaternion(quaternion, order);

    return {
        x: Number.isFinite(euler.x) ? MathUtils.radToDeg(euler.x) : 0,
        y: Number.isFinite(euler.y) ? MathUtils.radToDeg(euler.y) : 0,
        z: Number.isFinite(euler.z) ? MathUtils.radToDeg(euler.z) : 0,
    };
}

export function eulerToQuaternion(x: number, y: number, z: number, order: EulerOrder = 'XYZ'): Quaternion {
    const xRad = Number.isFinite(x) ? MathUtils.degToRad(x) : 0;
    const yRad = Number.isFinite(y) ? MathUtils.degToRad(y) : 0;
    const zRad = Number.isFinite(z) ? MathUtils.degToRad(z) : 0;

    const euler = new Euler(xRad, yRad, zRad, order);
    const quaternion = new ThreeQuaternion().setFromEuler(euler);
    quaternion.normalize();

    return {
        x: Number.isFinite(quaternion.x) ? quaternion.x : 0,
        y: Number.isFinite(quaternion.y) ? quaternion.y : 0,
        z: Number.isFinite(quaternion.z) ? quaternion.z : 0,
        w: Number.isFinite(quaternion.w) ? quaternion.w : 1,
    };
}
